import React from "react";
import useSEO from "../hooks/useSEO";
import PageHero from "../components/PageHero";

const sections = [
  {
    h: "1. Scope",
    p: [
      "This Privacy Policy explains how Fab Build Pro collects, uses, stores, protects, and shares information when you visit our website, submit a form, request an estimate or inspection, schedule an appointment, communicate with us, become a customer, or otherwise use our services.",
      "This policy applies to information collected through our website, online forms, telephone calls, text messages, email communications, advertising campaigns, customer relationship management systems, and other interactions with Fab Build Pro.",
    ],
  },
  {
    h: "2. Information We Collect",
    sub: [
      {
        h: "2.1 Information You Provide to Us",
        p: ["We may collect information that you choose to provide, including:"],
        list: [
          "Full name",
          "Email address",
          "Phone number",
          "Property or service address",
          "Mailing address",
          "ZIP code and service-area information",
          "Property ownership or occupancy information",
          "Services you are interested in",
          "Project descriptions, repair needs, questions, notes, and messages",
          "Appointment and scheduling information",
          "Photos, videos, documents, measurements, inspection information, and other materials related to your property or project",
          "Estimate, contract, invoice, payment, warranty, and customer-service information",
          "Insurance claim or policy-related information that you voluntarily provide in connection with storm damage or restoration services",
          "Referral information",
          "Communication preferences, including whether you opt in or opt out of SMS/text messages or marketing communications",
        ],
      },
      {
        h: "2.2 SMS Opt-In and Communication Records",
        p: ["If you consent to receive text messages from us, we may collect and retain records related to that consent, including:"],
        list: [
          "Your phone number",
          "The date, time, and method of opt-in",
          "The form, page, call, conversation, or source where consent was provided",
          "Your SMS preferences",
          "Message history",
          "Opt-out requests",
        ],
      },
      {
        h: "2.3 Information Collected Automatically",
        p: ["When you use our website, we may automatically collect certain information, including:"],
        list: [
          "IP address",
          "Device type",
          "Browser type",
          "Operating system",
          "Approximate location derived from your IP address",
          "Pages viewed",
          "Links clicked",
          "Form interactions",
          "Referring and exit pages",
          "Cookie identifiers",
          "Analytics information",
          "Advertising identifiers",
          "Conversion and campaign-tracking information",
        ],
        after: ["We do not sell your personal information."],
      },
    ],
  },
  {
    h: "3. How We Use Information",
    p: ["We may use the information we collect to:"],
    list: [
      "Respond to inquiries and requests",
      "Schedule inspections, estimates, consultations, appointments, and follow-ups",
      "Determine whether we provide services in your area",
      "Evaluate requested roofing, construction, remodeling, storm restoration, or other home-improvement services",
      "Prepare estimates, proposals, contracts, invoices, warranties, and project records",
      "Communicate about inspections, projects, scheduling, materials, payments, warranties, and customer service",
      "Provide requested services and project updates",
      "Communicate with you regarding storm damage or insurance-related projects when applicable",
      "Send transactional messages, reminders, confirmations, and requested information",
      "Send marketing communications when permitted, including email or SMS communications when you have provided the required consent",
      "Process payments and maintain business and accounting records",
      "Coordinate with subcontractors, suppliers, manufacturers, inspectors, consultants, financing providers, or other service providers when reasonably necessary to perform requested services",
      "Improve our website, services, advertising, lead forms, customer experience, and business operations",
      "Measure website traffic, advertising performance, leads, calls, conversions, and campaign effectiveness",
      "Prevent fraud, misuse, unauthorized activity, or security incidents",
      "Enforce agreements and protect our legal rights",
      "Comply with applicable laws, regulations, court orders, and lawful government requests",
    ],
  },
  {
    h: "4. How We Share Information",
    p: [
      "We may share personal information only when reasonably necessary to operate our business, provide requested services, or comply with legal obligations. This may include sharing information with:",
    ],
    list: [
      "Website hosting, CRM, email, SMS, telephone, scheduling, and customer communication providers",
      "Payment processors and accounting providers",
      "Analytics, advertising, and conversion-tracking providers",
      "Contractors, subcontractors, suppliers, manufacturers, inspectors, consultants, or other service providers involved in your project",
      "Financing providers when you request or apply for financing",
      "Insurance-related professionals or other parties when you specifically request, authorize, or require their involvement in connection with your project",
      "Professional advisers such as attorneys, accountants, or consultants",
      "Government agencies, courts, regulators, law enforcement, or other recipients when required by law or reasonably necessary to protect rights, safety, property, or legal interests",
    ],
    callout: {
      h: "SMS Opt-In Data and Consent",
      p: [
        "SMS opt-in data and consent are not sold, rented, shared, or disclosed to third parties or affiliates for their own marketing or promotional purposes.",
        "The categories of information sharing described above exclude text-message originator opt-in data and consent. SMS opt-in information may be provided only to vendors or service providers that help us deliver and operate our text-messaging program, and only as necessary for that purpose.",
      ],
    },
  },
  {
    h: "5. SMS/Text Message Opt-In Details",
    sub: [
      {
        h: "5.1 How You May Opt In",
        p: ["You may opt in to receive text messages from Fab Build Pro by:"],
        list: [
          "Submitting a website or advertising lead form that includes SMS consent",
          "Checking an SMS consent box where available",
          "Providing your phone number and expressly agreeing to receive text messages during a call or conversation",
          "Texting us first",
          "Otherwise providing permission for us to contact you by text",
        ],
      },
      {
        h: "5.2 Types of SMS Messages We May Send",
        p: ["If you opt in, we may send messages regarding:"],
        list: [
          "Estimate or inspection scheduling",
          "Appointment confirmations and reminders",
          "Follow-ups regarding your inquiry",
          "Project scheduling and updates",
          "Customer-service communications",
          "Estimates, proposals, contracts, or requested information",
          "Storm-damage or service follow-ups",
          "Warranty or project-related communication",
          "Marketing messages, promotions, seasonal offers, referral programs, or company updates when permitted by your consent",
        ],
      },
      {
        h: "5.3 SMS Terms",
        list: [
          "Message frequency varies.",
          "Message and data rates may apply.",
          "Reply STOP to unsubscribe from SMS messages.",
          "Reply HELP for assistance or contact us using the information below.",
          "Consent to receive marketing text messages is not a condition of purchasing goods or services from Fab Build Pro.",
        ],
      },
      {
        h: "5.4 SMS Opt-Out Handling",
        p: [
          "If you reply STOP, we may send one final confirmation message and then stop sending further SMS messages to that number unless you later opt in again.",
          "You may also contact us directly to update your phone number, change your communication preferences, or request removal from marketing communications.",
        ],
      },
    ],
  },
  {
    h: "6. Cookies, Analytics, Advertising, and Call Tracking",
    p: [
      "We may use cookies and similar technologies to operate our website, understand how visitors use it, improve website performance, measure advertising results, and support our marketing activities. These tools may include:",
    ],
    list: [
      "Website analytics tools",
      "Advertising and conversion-tracking platforms",
      "Remarketing or audience-measurement technologies",
      "Call-tracking systems",
      "CRM, form, scheduling, and automation platforms",
    ],
    after: [
      "These technologies may collect information such as your device, browser, IP address, pages viewed, referring source, form interactions, advertising interactions, and conversion activity.",
    ],
    sub: [
      {
        h: "6.1 Cookie Controls",
        p: [
          "You can control or delete cookies through your browser settings. Many browsers also allow you to block third-party cookies.",
          "Disabling cookies may cause some website features or tracking functions to work differently or become unavailable.",
        ],
      },
    ],
  },
  {
    h: "7. Financing and Third-Party Financial Services",
    p: [
      "Fab Build Pro may provide access to financing options through third-party lenders or financing providers.",
      "If you choose to apply for financing, the financing provider may collect personal, financial, credit, or other information directly from you under its own privacy policy and terms.",
      "Fab Build Pro does not control the privacy practices, credit decisions, underwriting standards, or data-security practices of independent financing providers.",
    ],
  },
  {
    h: "8. Data Security Practices",
    p: ["We use reasonable administrative, technical, and physical safeguards designed to protect personal information against unauthorized access, loss, misuse, alteration, or disclosure. These safeguards may include:"],
    list: [
      "Using reputable hosting, CRM, communication, and technology providers",
      "Limiting access to personal information to employees, contractors, and vendors who reasonably need it",
      "Using passwords, permissions, access controls, and account-security measures where available",
      "Using secure systems for storing or transmitting information where reasonably available",
      "Limiting vendor access to information where practical",
    ],
    after: [
      "No method of electronic transmission or storage is completely secure. We cannot guarantee absolute security, but we take reasonable steps to protect the information entrusted to us.",
    ],
  },
  {
    h: "9. Data Retention",
    p: ["We retain personal information for as long as reasonably necessary for the purposes described in this policy. This may include retaining information to:"],
    list: [
      "Provide and complete services",
      "Maintain estimates, contracts, invoices, warranties, project documentation, and customer records",
      "Maintain accounting and tax records",
      "Maintain insurance, licensing, or construction-related records",
      "Document SMS and marketing consent or opt-out requests",
      "Resolve disputes",
      "Enforce agreements",
      "Protect legal rights",
      "Comply with applicable laws and recordkeeping obligations",
    ],
  },
  {
    h: "10. Your Choices and Privacy Rights",
    p: ["Depending on applicable law and subject to legal, contractual, operational, and recordkeeping requirements, you may contact us to request that we:"],
    list: [
      "Provide access to personal information we maintain about you",
      "Correct or update inaccurate personal information",
      "Delete certain personal information",
      "Update your contact information",
      "Change your communication preferences",
      "Stop sending marketing communications",
    ],
    sub: [
      {
        h: "10.1 How to Unsubscribe or Opt Out",
        p: [
          "SMS: Reply STOP to any text message.",
          "Email: Use the unsubscribe link included in marketing emails when available or contact us directly.",
          "Cookies: Adjust your browser settings to block or delete cookies.",
        ],
      },
      {
        h: "10.2 How to Submit a Privacy Request",
        p: [
          "Email: info@fabbuildpro.com",
          "Phone: 703-766-6599",
          "We may need to verify your identity before processing certain privacy requests.",
        ],
      },
    ],
  },
  {
    h: "11. Third-Party Links and Services",
    p: [
      "Our website may contain links to third-party websites, financing providers, manufacturers, social media platforms, review websites, payment services, scheduling systems, insurance-related resources, or other external services.",
      "Those third parties operate independently from Fab Build Pro and may have their own privacy policies and terms.",
      "We are not responsible for the privacy practices, security, availability, or content of third-party websites or services. You should review their privacy policies before providing personal information.",
    ],
  },
  {
    h: "12. Children's Privacy",
    p: [
      "Our website and services are intended for homeowners, property owners, businesses, and other adults seeking construction or home-improvement services.",
      "They are not directed to children under 13, and we do not knowingly collect personal information from children under 13.",
      "If we learn that we have collected personal information from a child under 13, we will take reasonable steps to delete it.",
    ],
  },
  {
    h: "13. Changes to This Privacy Policy",
    p: [
      "We may update this Privacy Policy from time to time to reflect changes in our services, technology, business practices, or legal requirements.",
      "When we update the policy, we will post the revised version on our website and update the Effective Date.",
    ],
  },
];

const Block = ({ data }) => (
  <div className="mb-8">
    <h2 className="font-display font-bold text-brand-navy text-xl md:text-2xl mb-3">{data.h}</h2>
    {data.p && data.p.map((t, i) => (
      <p key={i} className="text-slate-600 leading-relaxed mb-3">{t}</p>
    ))}
    {data.list && (
      <ul className="list-disc pl-6 space-y-1.5 text-slate-600 mb-3">
        {data.list.map((li, i) => <li key={i}>{li}</li>)}
      </ul>
    )}
    {data.after && data.after.map((t, i) => (
      <p key={i} className="text-slate-600 leading-relaxed mb-3 font-medium">{t}</p>
    ))}
    {data.callout && (
      <div className="bg-brand-cream border-l-4 border-brand-orange rounded-r-xl p-5 my-4">
        <h3 className="font-display font-bold text-brand-navy mb-2">{data.callout.h}</h3>
        {data.callout.p.map((t, i) => (
          <p key={i} className="text-slate-600 leading-relaxed mb-2 text-[15px]">{t}</p>
        ))}
      </div>
    )}
    {data.sub && data.sub.map((s, i) => (
      <div key={i} className="mt-5">
        <h3 className="font-display font-semibold text-brand-navy text-lg mb-2">{s.h}</h3>
        {s.p && s.p.map((t, j) => (
          <p key={j} className="text-slate-600 leading-relaxed mb-2">{t}</p>
        ))}
        {s.list && (
          <ul className="list-disc pl-6 space-y-1.5 text-slate-600 mb-2">
            {s.list.map((li, j) => <li key={j}>{li}</li>)}
          </ul>
        )}
        {s.after && s.after.map((t, j) => (
          <p key={j} className="text-slate-600 leading-relaxed mb-2 font-medium">{t}</p>
        ))}
      </div>
    ))}
  </div>
);

const PrivacyPolicy = () => {
  useSEO({
    title: "Privacy Policy | Fab Build Pro",
    description: "Fab Build Pro Privacy Policy — how we collect, use, protect, and share your information, including SMS opt-in and consent details.",
    crumbs: [{ name: "Privacy Policy", path: "/privacy-policy" }],
  });

  return (
    <>
      <PageHero title="Privacy Policy" subtitle="How we collect, use, protect, and share your information." />
      <section className="bg-white py-16" data-testid="privacy-policy-page">
        <div className="max-w-3xl mx-auto px-6">
          <p className="text-slate-500 text-sm mb-1"><strong>Effective Date:</strong> August 18, 2026</p>
          <p className="text-slate-500 text-sm mb-8">
            <strong>Fab Build Pro LLC</strong> (“Fab Build Pro,” “we,” “us,” or “our”) · Website: https://fabbuildpro.com
          </p>

          {sections.map((s, i) => <Block key={i} data={s} />)}

          <div className="mt-10 pt-8 border-t border-black/10">
            <h2 className="font-display font-bold text-brand-navy text-xl md:text-2xl mb-3">14. Contact Us</h2>
            <div className="text-slate-600 leading-relaxed space-y-0.5">
              <p className="font-semibold text-brand-navy">Fab Build Pro LLC</p>
              <p>1934 Old Gallows Rd STE 350</p>
              <p>Vienna, VA 22182, United States</p>
              <p className="mt-2">Website: <a href="https://fabbuildpro.com" className="text-brand-orange hover:underline">https://fabbuildpro.com</a></p>
              <p>Phone: <a href="tel:+17037666599" className="text-brand-orange hover:underline">703-766-6599</a></p>
              <p>Email: <a href="mailto:info@fabbuildpro.com" className="text-brand-orange hover:underline">info@fabbuildpro.com</a></p>
            </div>
            <p className="text-slate-400 text-sm mt-6">© 2026 Fab Build Pro LLC. All rights reserved.</p>
          </div>
        </div>
      </section>
    </>
  );
};

export default PrivacyPolicy;
